import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const ContactPageArabic = () => {
  const { i18n } = useTranslation();
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    company: '',
    email: '',
    fleetSize: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Set language to Arabic when component mounts
  useEffect(() => {
    if (i18n.language !== 'ar') {
      i18n.changeLanguage('ar');
    }
  }, [i18n]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!contactForm.name.trim() || !contactForm.phone.trim()) {
      toast.error('يرجى ملء جميع الحقول المطلوبة.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('Submitting contact form data:', contactForm);
      
      const response = await fetch('https://formspree.io/f/mdkglkeg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: contactForm.name.trim(),
          phone: contactForm.phone.trim(),
          email: contactForm.email.trim(),
          company: contactForm.company.trim(),
          fleetSize: contactForm.fleetSize,
          message: contactForm.message.trim() || 'طلب تواصل من صفحة الاتصال',
          _subject: `طلب تواصل جديد من ${contactForm.name}`,
          _captcha: false
        })
      });

      const responseData = await response.json();
      console.log('Contact response:', response.status, responseData);

      if (response.ok) {
        setIsSuccess(true);
        toast.success('تم إرسال رسالتك بنجاح! سنتواصل معك قريباً.');
        setContactForm({ name: '', phone: '', company: '', email: '', fleetSize: '', message: '' });
      } else {
        throw new Error(responseData.error || 'Failed to send request');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      toast.error('حدث خطأ أثناء إرسال رسالتك. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-gray-900">Logistiat</Link>
            </div>
            <div className="hidden md:flex space-x-8 space-x-reverse">
              <Link to="/ar/features" className="text-gray-600 hover:text-gray-900">المميزات</Link>
              <Link to="/ar/pricing" className="text-gray-600 hover:text-gray-900">الأسعار</Link>
              <Link to="/ar/trip-costing" className="text-gray-600 hover:text-gray-900">احتساب التكلفة</Link>
              <Link to="/ar/contact" className="text-blue-600 font-medium">اتصل بنا</Link>
              <Link to="/en" className="text-gray-600 hover:text-gray-900">English</Link>
            </div>
            <div className="flex space-x-4 space-x-reverse">
              <Link
                to="/login"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium"
              >
                تسجيل الدخول
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            اتصل بنا
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            تواصل مع فريقنا لمعرفة المزيد عن برنامج إدارة الأسطول Logistiat
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">تواصل معنا</h2>
              <p className="text-lg text-gray-600 mb-8">
                مستعد لتحويل عمليات أسطولك؟ تواصل معنا للحصول على عرض توضيحي مخصص واكتشف كيف يمكن لـ Logistiat مساعدة عملك.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="text-2xl ml-4">📞</div>
                  <div>
                    <h3 className="font-semibold">الهاتف</h3>
                    <p className="text-gray-600">0544288686</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="text-2xl ml-4">📧</div>
                  <div>
                    <h3 className="font-semibold">البريد الإلكتروني</h3>
                    <p className="text-gray-600">info@logistiat.app</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="text-2xl ml-4">🏢</div>
                  <div>
                    <h3 className="font-semibold">الموقع</h3>
                    <p className="text-gray-600">المملكة العربية السعودية</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8">
              {isSuccess ? (
                <div className="text-center py-8">
                  <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">تم الإرسال بنجاح!</h4>
                  <p className="text-gray-600 mb-4">
                    تم إرسال رسالتك بنجاح. سنتواصل معك قريباً.
                  </p>
                  <button
                    onClick={() => setIsSuccess(false)}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    إرسال رسالة أخرى
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الاسم *
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="الاسم"

                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الهاتف *
                    </label>
                    <input
                      type="tel"
                      required
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+966 50 000 0000"

                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      البريد الإلكتروني
                    </label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="your@email.com"

                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الشركة
                    </label>
                    <input
                      type="text"
                      value={contactForm.company}
                      onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="اسم شركتك"

                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      حجم الأسطول
                    </label>
                    <select 
                      value={contactForm.fleetSize}
                      onChange={(e) => setContactForm({...contactForm, fleetSize: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">اختر حجم الأسطول</option>
                      <option value="1-10">1-10 مركبات</option>
                      <option value="11-50">11-50 مركبة</option>
                      <option value="51-100">51-100 مركبة</option>
                      <option value="100+">أكثر من 100 مركبة</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الرسالة
                    </label>
                    <textarea
                      rows={4}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="أخبرنا عن احتياجات إدارة الأسطول لديك..."

                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'جاري الإرسال...' : 'إرسال الرسالة'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPageArabic;